import { useChatStore } from '../state/chatStore'
import { voiceDiag } from './voiceDiagnostics'

/**
 * Ein Bild — aus der laufenden Kamera oder als Foto hochgeladen — wird
 * ausgewertet und beantwortet. Ein Weg für drei Auslöser: den Knopf „Was
 * siehst du gerade?", ein hochgeladenes Foto, und die Frage im Kamera-Modus.
 *
 * Zuerst die Showcase-Erkennung (klein, schnell, günstig). Trifft sie, ist
 * das die Antwort: Der Besucher steht vor einem Showcase, und K4ia erklärt
 * ihn. Trifft sie nicht, geht das Bild in größerer Auflösung an das
 * Sprachmodell mit der Frage, was zu sehen ist.
 *
 * **Sprachmodus:** Läuft ein Gespräch, soll K4ia das auch *sagen*, nicht nur
 * schreiben. Ein Bild lässt sich nicht über ElevenLabs schicken, ein Text
 * schon: Beim Showcase geht `[[kamera:<id>]]`, beim freien Bild erst die
 * Text-Antwort in den Chat und danach `[[foto]]` — die Brücke übersetzt beides,
 * und K4ia spricht, was sie gerade geschrieben hat.
 *
 * Kein Bild wird gespeichert (FR-34): Es lebt in einer Data-URL, geht auf die
 * Reise und ist danach weg.
 */

export interface RecognitionResult {
  showcaseId: string | null
  confidence: number
  alternatives: { showcaseId: string; confidence: number }[]
  debug?: string
}

export type Analyseergebnis =
  | { art: 'showcase'; id: string; title: string }
  /** Mehrere Showcases im Bild — die ganze Vitrine. K4ia fragt, welcher es sein soll. */
  | { art: 'mehrere'; ids: string[]; titles: string[] }
  | { art: 'beschrieben' }
  | { art: 'nichts' }
  | { art: 'fehler'; message: string }

export interface AnalyseOptionen {
  /**
   * `kamera`: Der Knopf „Was siehst du gerade?". Nur ein erkannter Showcase
   * ist eine Antwort — sonst passiert im Chat nichts, und die Kamera bleibt.
   * Eine allgemeine Bildbeschreibung mitten im Gespräch brachte K4ia
   * durcheinander, im Sprachmodus doppelt.
   * `foto`: Ein bewusst geschicktes Bild. Ohne Showcase wird es beschrieben.
   */
  modus: 'kamera' | 'foto'
  /**
   * Welches Sprachmodell die zweite Stufe nimmt: `fast` für den ersten Blick
   * beim Öffnen der Kamera (schnell, günstig), `strong` für den Knopf — der
   * Besucher hat ausdrücklich gefragt, da zählt Treffsicherheit.
   */
  stage2?: 'fast' | 'strong'
  /** Länger wartet der Aufruf nicht — danach gilt: nichts erkannt. */
  timeoutMs?: number
}

interface KatalogEintrag {
  id: string
  slug: string
  title: string
}

let katalogCache: KatalogEintrag[] | null = null

/** Die Live-Showcases (ID, Slug, Titel), einmal je Seite geladen. */
export async function loadShowcaseCatalog(): Promise<KatalogEintrag[]> {
  if (katalogCache) return katalogCache
  let katalog: KatalogEintrag[] = []
  try {
    const res = await fetch('/api/v1/showcases')
    if (res.ok) {
      const { showcases } = (await res.json()) as { showcases: KatalogEintrag[] }
      katalog = showcases.map((s) => ({ id: s.id, slug: s.slug, title: s.title }))
    }
  } catch {
    // Ohne Katalog heißt der Showcase im Text eben „diesen Showcase".
  }
  katalogCache = katalog
  return katalog
}

/** Titel je ID — für die Kamera-Überlagerung. */
export async function loadShowcaseTitles(): Promise<Map<string, string>> {
  return new Map((await loadShowcaseCatalog()).map((s) => [s.id, s.title]))
}

/** Nur für Tests. */
export function resetShowcaseTitlesForTests(): void {
  katalogCache = null
}

/**
 * Hat das Sprachmodell in seiner Antwort einen Showcase geladen? Dann hat es
 * ihn auf dem Bild erkannt — das große Modell sieht mehr als das kleine, das
 * die Kamera zuerst fragt. Der Werkzeugaufruf ist das Zeichen, nicht der Text.
 */
export async function showcaseAusLetzterAntwort(): Promise<KatalogEintrag | null> {
  const letzte = [...useChatStore.getState().messages].reverse().find((m) => m.role === 'assistant')
  const aufruf = letzte?.toolCalls.find((c) => c.name === 'get_showcase')
  const gesucht = (aufruf?.input as { showcaseIdOrSlug?: string } | undefined)?.showcaseIdOrSlug
  if (!gesucht) return null
  const katalog = await loadShowcaseCatalog()
  return katalog.find((s) => s.id === gesucht || s.slug === gesucht) ?? null
}

/** Ein verkleinertes JPEG aus einer Bildquelle — Video oder geladenes Bild. */
export function frameFrom(
  source: HTMLVideoElement | HTMLImageElement | ImageBitmap,
  maxWidth: number,
  quality = 0.8,
): string {
  const breite =
    'videoWidth' in source
      ? source.videoWidth
      : 'naturalWidth' in source
        ? source.naturalWidth
        : source.width
  const hoehe =
    'videoHeight' in source
      ? source.videoHeight
      : 'naturalHeight' in source
        ? source.naturalHeight
        : source.height
  const canvas = document.createElement('canvas')
  const faktor = Math.min(1, maxWidth / Math.max(1, breite))
  canvas.width = Math.round(breite * faktor)
  canvas.height = Math.round(hoehe * faktor)
  canvas.getContext('2d')?.drawImage(source, 0, 0, canvas.width, canvas.height)
  return canvas.toDataURL('image/jpeg', quality)
}

/** Ein hochgeladenes Foto in zwei Größen: klein für die Erkennung, größer fürs Modell. */
export async function framesFromFile(file: File): Promise<{ klein: string; gross: string }> {
  const bitmap = await createImageBitmap(file)
  try {
    return { klein: frameFrom(bitmap, 512), gross: frameFrom(bitmap, 768, 0.85) }
  } finally {
    bitmap.close()
  }
}

/** Länger wartet niemand vor einem Stand. Danach gilt: nichts erkannt. */
const ERKENNUNG_ZEITGRENZE_MS = 25_000

export async function recognizeShowcase(
  dataUrl: string,
  thorough = false,
  source: 'loop' | 'button' | 'photo' = 'button',
  optionen: { stage2?: 'fast' | 'strong'; timeoutMs?: number } = {},
): Promise<RecognitionResult | null> {
  // Mit Zeitgrenze: Bleibt die Antwort aus, hing sonst „K4ia schaut hin …"
  // für immer im Kamerabild — und der Besucher mit ihm.
  const res = await fetch('/api/v1/vision/recognize', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({
      imageDataUrl: dataUrl,
      thorough,
      source,
      ...(optionen.stage2 ? { stage2: optionen.stage2 } : {}),
    }),
    signal: AbortSignal.timeout(optionen.timeoutMs ?? ERKENNUNG_ZEITGRENZE_MS),
  })
  if (!res.ok) {
    voiceDiag.kameraLetzte = `HTTP ${res.status} ${(await res.text().catch(() => '')).slice(0, 80)}`
    return null
  }
  const result = (await res.json()) as RecognitionResult
  voiceDiag.kameraLetzte = `${result.showcaseId ?? 'nichts'} (${result.confidence.toFixed(2)})${
    result.debug ? ` — ${result.debug}` : ''
  }`
  return result
}

/**
 * Wertet ein Bild aus und lässt K4ia antworten — im Chat, und im Sprachmodus
 * auch gesprochen.
 */
export async function analyzeImage(
  bilder: { klein: string; gross: string },
  optionen: AnalyseOptionen = { modus: 'foto' },
): Promise<Analyseergebnis> {
  const store = useChatStore.getState()
  const sprecher = store.voiceMarkerSender
  const vorher = store.avatarState

  // Sofort sichtbar, dass K4ia hinschaut: Kopfzeile „denkt nach". Beim Foto
  // dazu eine leere Sprechblase mit der Figur; im Kamera-Modus zeigt die
  // Kamera selbst, dass geschaut wird — der Chat bleibt ruhig, bis es etwas
  // zu sagen gibt.
  store.setAvatarState('thinking')
  const platzhalter = optionen.modus === 'foto' ? store.beginVoiceTurn() : null

  let erkannt: RecognitionResult | null = null
  try {
    // Gründlich: Sagt das kleine Modell „keiner", fragt der Worker das große.
    erkannt = await recognizeShowcase(
      bilder.klein,
      true,
      optionen.modus === 'foto' ? 'photo' : 'button',
      { stage2: optionen.stage2, timeoutMs: optionen.timeoutMs },
    )
  } catch (err) {
    voiceDiag.kameraLetzte = `Erkennung abgebrochen: ${err instanceof Error ? err.message : String(err)}`
    erkannt = null
  }
  // Die echte Antwort bringt ihre eigene Blase mit (Chat wie Sprache).
  if (platzhalter) store.dropMessage(platzhalter)

  if (erkannt?.showcaseId) {
    const titel = (await loadShowcaseTitles()).get(erkannt.showcaseId) ?? 'diesen Showcase'
    if (sprecher) {
      store.addUserTranscript(`Ich stehe vor: ${titel}`)
      sprecher(`[[kamera:${erkannt.showcaseId}]]`)
    } else {
      await store.sendMessage(`Ich stehe vor: ${titel}`, erkannt.showcaseId)
    }
    return { art: 'showcase', id: erkannt.showcaseId, title: titel }
  }

  // Mehrere zugleich — der Besucher filmt die ganze Vitrine. Keiner davon ist
  // „der" Showcase; K4ia sagt, was zu sehen ist, und fragt, welcher es sein
  // soll. Die Vorschläge darunter machen die Antwort zu einem Tippen.
  if (erkannt && !erkannt.showcaseId && erkannt.alternatives.length >= 2) {
    const titelVon = await loadShowcaseTitles()
    const ids = erkannt.alternatives.map((a) => a.showcaseId)
    const titles = ids.map((id) => titelVon.get(id) ?? 'ein Showcase')
    const satz = `Ich stehe vor: ${listeVon(titles)}`
    if (sprecher) {
      store.addUserTranscript(satz)
      sprecher(`[[kamera:${ids.join(',')}]]`)
    } else {
      await store.sendMessage(satz, undefined, undefined, ids)
    }
    useChatStore.setState({ suggestions: titles.map((t) => `Erzähl mir von: ${t}`) })
    return { art: 'mehrere', ids, titles }
  }

  // Kamera-Knopf ohne Treffer: nichts in den Chat, nichts an ElevenLabs. Die
  // Kamera sagt es dem Besucher an Ort und Stelle.
  if (optionen.modus === 'kamera') {
    store.setAvatarState(vorher === 'thinking' ? 'idle' : vorher)
    return { art: 'nichts' }
  }

  // Foto ohne Showcase: das Bild selbst beschreiben lassen. Erst der Text,
  // dann — im Sprachmodus — die Bitte, ihn zu sprechen; die Beschreibung
  // steht dann schon im Verlauf, aus dem die Sprachantwort schöpft.
  await store.sendMessage('Was siehst du auf diesem Bild?', undefined, bilder.gross)
  if (useChatStore.getState().errorMessage) {
    return { art: 'fehler', message: useChatStore.getState().errorMessage ?? 'Fehler' }
  }
  if (sprecher) sprecher('[[foto]]')

  // Das kleine Modell sagte „keiner" — aber das große hat vielleicht doch
  // einen Showcase erkannt und geladen. Dann zählt das: Die Kamera darf zu.
  const erkanntVomModell = await showcaseAusLetzterAntwort()
  if (erkanntVomModell) {
    return { art: 'showcase', id: erkanntVomModell.id, title: erkanntVomModell.title }
  }
  return { art: 'beschrieben' }
}

/** „A und B" bzw. „A, B und C". */
export function listeVon(titel: string[]): string {
  if (titel.length <= 1) return titel[0] ?? ''
  return `${titel.slice(0, -1).join(', ')} und ${titel[titel.length - 1]!}`
}
